import { useCallback, useEffect, useState } from 'react';
import { isApiError } from '@aiptyg/types';
import * as settingsService from '../services/settings.service';
import type {
  MarketConfigDTO,
  MarketConfigUpdateParams,
} from '../types/settings.types';
import { useToastStore } from '../../../store/toast.store';

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    const map: Record<string, string> = {
      admin_role_required: '无管理员权限',
      network_error: '网络连接失败，请检查网络',
      invalid_market_config: '市场配置无效',
    };
    return map[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

export function useMarketConfigs() {
  const addToast = useToastStore((s) => s.addToast);
  const [items, setItems] = useState<MarketConfigDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingMarketId, setSavingMarketId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await settingsService.getMarketConfigs();
      setItems(result.items ?? result.markets ?? []);
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = useCallback(async (marketId: string, params: MarketConfigUpdateParams) => {
    setSavingMarketId(marketId);
    try {
      const result = await settingsService.updateMarketConfig(marketId, params);
      setItems(result.items ?? result.markets ?? []);
      addToast('success', '市场配置已保存');
    } catch (e) {
      addToast('error', resolveError(e));
    } finally {
      setSavingMarketId(null);
    }
  }, [addToast]);

  return {
    items,
    loading,
    savingMarketId,
    error,
    reload: load,
    save,
  };
}
